import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";

// Action types
import * as types from "../actionTypes/notifications.types";

// Actions
import {
  fetchNotificationsSuccess,
  fetchUnreadCountSuccess,
  fetchUnreadCount,
  updateEnabledNotificationsSuccess,
  fetchEnabledNotificationsSuccess
} from "../actions/notifications.actions";

// Calls
import {
  fetchNotificationsRequest,
  fetchUnreadCountRequest,
  markNotificationSeenRequest,
  markAllNotificationsSeenRequest,
  viewNotificationRequest,
  fetchEnabledNotificationsRequest,
  updateEnabledNotificationsRequest
} from "../../api/calls/notifications.call";

const PER_PAGE = 20;

function* fetchNotificationsCall(action) {
  const response = yield call(fetchNotificationsRequest, {
    perPage: PER_PAGE,
    from: action.from
  });
  if (response.hasError) {
    return yield put({ type: types.FETCH_NOTIFICATIONS_FAILED });
  }
  yield put(
    fetchNotificationsSuccess(
      response.notifications,
      response.pagination?.has_more ?? false,
      !!action.from
    )
  );
}

function* fetchNotificationsWatcher() {
  yield takeLatest(types.FETCH_NOTIFICATIONS_REQUEST, fetchNotificationsCall);
}

// Polled every minute — fails silently to avoid toast noise.
function* fetchUnreadCountCall() {
  const response = yield call(fetchUnreadCountRequest);
  if (response.hasError) return;
  yield put(fetchUnreadCountSuccess(response.count));
}

function* fetchUnreadCountWatcher() {
  yield takeLatest(types.FETCH_UNREAD_COUNT_REQUEST, fetchUnreadCountCall);
}

function* markNotificationSeenCall(action) {
  const response = yield call(markNotificationSeenRequest, action.id);
  // Shared seen semantics: a teammate marking it first returns 400
  // "already been seen" — that's a success for us.
  if (
    response.hasError &&
    !response.error?.message?.includes("already been seen")
  ) {
    yield put(fetchUnreadCount());
  }
}

function* markNotificationSeenWatcher() {
  yield takeEvery(types.MARK_NOTIFICATION_SEEN_REQUEST, markNotificationSeenCall);
}

function* markAllNotificationsSeenCall() {
  const response = yield call(markAllNotificationsSeenRequest);
  if (response.hasError) {
    toast.error(response.error.message);
    yield put(fetchUnreadCount());
  }
}

function* markAllNotificationsSeenWatcher() {
  yield takeLatest(
    types.MARK_ALL_NOTIFICATIONS_SEEN_REQUEST,
    markAllNotificationsSeenCall
  );
}

// Fire-and-forget viewer tracking ("seen by" avatars for teammates).
function* viewNotificationCall(action) {
  yield call(viewNotificationRequest, action.id);
}

function* viewNotificationWatcher() {
  yield takeEvery(types.VIEW_NOTIFICATION_REQUEST, viewNotificationCall);
}

function* fetchEnabledNotificationsCall() {
  const response = yield call(fetchEnabledNotificationsRequest);
  if (response.hasError) {
    toast.error(response.error.message);
    return yield put({ type: types.FETCH_ENABLED_NOTIFICATIONS_FAILED });
  }
  yield put(fetchEnabledNotificationsSuccess(response.notifications));
}

function* fetchEnabledNotificationsWatcher() {
  yield takeLatest(
    types.FETCH_ENABLED_NOTIFICATIONS_REQUEST,
    fetchEnabledNotificationsCall
  );
}

function* updateEnabledNotificationsCall(action) {
  const response = yield call(updateEnabledNotificationsRequest, action.prefs);
  if (response.hasError) {
    toast.error(response.error.message);
    return yield put({ type: types.UPDATE_ENABLED_NOTIFICATIONS_FAILED });
  }
  toast.success("Notification preferences saved");
  yield put(updateEnabledNotificationsSuccess(response.notifications));
}

function* updateEnabledNotificationsWatcher() {
  yield takeLatest(
    types.UPDATE_ENABLED_NOTIFICATIONS_REQUEST,
    updateEnabledNotificationsCall
  );
}

export default function* notificationsSaga() {
  yield all([
    fetchNotificationsWatcher(),
    fetchUnreadCountWatcher(),
    markNotificationSeenWatcher(),
    markAllNotificationsSeenWatcher(),
    viewNotificationWatcher(),
    fetchEnabledNotificationsWatcher(),
    updateEnabledNotificationsWatcher()
  ]);
}
